/**
 * Prefix every selector in a stylesheet with `scope`, so CSS written for a whole
 * classic page only reaches the converted rows rendered inside `scope`.
 *
 * - `html`, `body` and `:root` stand for the page itself, so they become the
 *   scope element (`body .x` → `.scope .x`, `body` → `.scope`).
 * - Selectors inside @media / @supports / @layer / @document are scoped too.
 * - @keyframes, @font-face and other descriptor blocks are kept as they are.
 * - @import is moved to the top (it is ignored anywhere else); @charset is
 *   dropped (meaningless inside a <style> element).
 * - Comments are removed; strings, url(...) and escapes are respected.
 *
 * It is a small hand-written scanner rather than a full CSS parser: it only needs
 * to find rule boundaries and selector lists, never to understand declarations.
 */
export function scopeCss(css: string, scope: string): string {
  if (!css || !css.trim()) {
    return '';
  }

  const imports: string[] = [];
  const body = scopeBlock(stripComments(css), scope, imports);
  return [...imports, body].filter((part) => part.trim()).join('\n');
}

const NESTED_AT_RULES = ['@media', '@supports', '@layer', '@document', '@-moz-document', '@container'];

function scopeBlock(css: string, scope: string, imports: string[]): string {
  const out: string[] = [];
  let i = 0;

  while (i < css.length) {
    // Prelude: everything up to the next `{` or `;` at this level
    const preludeEnd = findNext(css, i, ['{', ';', '}']);
    if (preludeEnd === -1) {
      // Trailing text without a block: not a rule, drop it
      break;
    }

    const prelude = css.slice(i, preludeEnd).trim();
    const terminator = css[preludeEnd];

    if (terminator === '}') {
      // Stray closing brace (malformed input): skip it
      i = preludeEnd + 1;
      continue;
    }

    if (terminator === ';') {
      // Statement at-rule (@import, @charset, @namespace) or junk
      const lower = prelude.toLowerCase();
      if (lower.startsWith('@import')) {
        imports.push(prelude + ';');
      } else if (prelude.startsWith('@') && !lower.startsWith('@charset')) {
        out.push(prelude + ';');
      }
      i = preludeEnd + 1;
      continue;
    }

    const blockEnd = findMatchingBrace(css, preludeEnd);
    const inner = css.slice(preludeEnd + 1, blockEnd === -1 ? css.length : blockEnd);
    i = blockEnd === -1 ? css.length : blockEnd + 1;

    if (!prelude) {
      continue;
    }

    if (prelude.startsWith('@')) {
      const name = prelude.split(/[\s({]/)[0].toLowerCase();
      if (NESTED_AT_RULES.indexOf(name) !== -1) {
        out.push(`${prelude} {\n${scopeBlock(inner, scope, imports)}\n}`);
      } else {
        // @keyframes, @font-face, @page, @property, @counter-style...
        out.push(`${prelude} {${inner}}`);
      }
      continue;
    }

    out.push(`${scopeSelectorList(prelude, scope)} {${inner}}`);
  }

  return out.join('\n');
}

export function scopeSelectorList(selectorList: string, scope: string): string {
  return splitTopLevel(selectorList, ',')
    .map((selector) => scopeSelector(selector.trim(), scope))
    .filter((selector) => selector)
    .join(', ');
}

function scopeSelector(selector: string, scope: string): string {
  if (!selector) {
    return '';
  }
  if (selector === scope || selector.startsWith(scope + ' ') || selector.startsWith(scope + '.') || selector.startsWith(scope + ':')) {
    return selector;
  }

  // Leading html / body / :root compounds (with whatever classes or attributes
  // they carry, e.g. `body.clean-body`) stand for the page: replace them with
  // the scope. `html body .x` → `.scope .x`.
  const pageRoot = /^(?:(?:html|body|:root)(?![\w-])(?:[.#[:][^\s>+~]*)?\s*(?:>\s*)?)+/i;
  const match = selector.match(pageRoot);
  if (match) {
    const rest = selector.slice(match[0].length).trim();
    return rest ? `${scope} ${rest}` : scope;
  }

  return `${scope} ${selector}`;
}

/** Index of the first of `chars` at depth 0 from `start`, skipping strings and parentheses. */
function findNext(css: string, start: number, chars: string[]): number {
  let parens = 0;
  for (let i = start; i < css.length; i++) {
    const ch = css[i];
    if (ch === '\\') {
      i++;
      continue;
    }
    if (ch === '"' || ch === "'") {
      i = skipString(css, i);
      continue;
    }
    if (ch === '(') {
      parens++;
      continue;
    }
    if (ch === ')') {
      parens = Math.max(0, parens - 1);
      continue;
    }
    if (parens === 0 && chars.indexOf(ch) !== -1) {
      return i;
    }
  }
  return -1;
}

/** Index of the `}` closing the `{` at `open`, or -1 when the block never closes. */
function findMatchingBrace(css: string, open: number): number {
  let depth = 0;
  for (let i = open; i < css.length; i++) {
    const ch = css[i];
    if (ch === '\\') {
      i++;
      continue;
    }
    if (ch === '"' || ch === "'") {
      i = skipString(css, i);
      continue;
    }
    if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
}

/** Index of the quote closing the string that opens at `start`. */
function skipString(css: string, start: number): number {
  const quote = css[start];
  for (let i = start + 1; i < css.length; i++) {
    if (css[i] === '\\') {
      i++;
      continue;
    }
    if (css[i] === quote || css[i] === '\n') {
      return i;
    }
  }
  return css.length - 1;
}

function splitTopLevel(text: string, separator: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\\') {
      current += ch + (text[i + 1] ?? '');
      i++;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const end = skipString(text, i);
      current += text.slice(i, end + 1);
      i = end;
      continue;
    }
    if (ch === '(' || ch === '[') {
      depth++;
    } else if (ch === ')' || ch === ']') {
      depth = Math.max(0, depth - 1);
    }
    if (ch === separator && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts;
}

function stripComments(css: string): string {
  let out = '';
  for (let i = 0; i < css.length; i++) {
    const ch = css[i];
    if (ch === '"' || ch === "'") {
      const end = skipString(css, i);
      out += css.slice(i, end + 1);
      i = end;
      continue;
    }
    if (ch === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2);
      i = end === -1 ? css.length : end + 1;
      continue;
    }
    out += ch;
  }
  return out;
}
